import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, Zap, AlertTriangle, Phone, Clock, Wind, Droplet,
  Syringe, Activity, ClipboardCheck, Target, CheckCircle2, Users,
} from 'lucide-react';
import Header from '../components/Header';

const RED = '#DC2626';
const DEEP_RED = '#991B1B';
const AMBER = '#D97706';
const BLUE = '#0284C7';
const GREEN = '#16A34A';
const NAVY = '#0E2D5C';

const PAEDIATRIC = [
  { w: '<10 kg', dose: '0.1 mg', vol: '0.1 mL' },
  { w: '10-20 kg', dose: '0.15 mg', vol: '0.15 mL' },
  { w: '20-50 kg', dose: '0.3 mg', vol: '0.3 mL' },
  { w: '>50 kg', dose: '0.5 mg', vol: '0.5 mL' },
];

const SECOND_LINE = [
  { icon: Wind, label: 'Oxygen', body: 'High flow via mask · Target SpO₂ > 94%' },
  { icon: Droplet, label: 'IV Fluids', body: 'Normal saline bolus · Adults 500-1000 mL rapidly · Children 10-20 mL/kg' },
  { icon: Syringe, label: 'Antihistamines', body: 'Adjunct only · Do NOT delay adrenaline' },
  { icon: Clock, label: 'Steroids', body: 'Delayed effect · Used AFTER stabilisation' },
];

const TRAY = [
  { n: 1, label: 'ADRENALINE (front & centre)', detail: '3 × ampoules 1:1000 · 3 × 1 mL syringes · 3 × 23G IM needles', tone: RED, highlight: true },
  { n: 2, label: 'Syringes + needles', detail: '(ready to load the adrenaline ampoule)', tone: RED },
  { n: 3, label: 'Oxygen', detail: 'Mask + tubing', tone: BLUE },
  { n: 4, label: 'IV Fluids', detail: 'IV cannulas (various sizes) · NS 1 L bag · giving set', tone: BLUE },
  { n: 5, label: 'Adjunct meds', detail: 'Antihistamine · Steroid', tone: AMBER },
];

const DRILL = [
  { range: '0-1', heading: 'Recognition', body: 'Radiographer recognises deterioration. Calls:', call: 'ANAPHYLAXIS — NEED HELP NOW' },
  { range: '1-2', heading: 'Prepare', body: 'Stop contrast · lay patient flat · prepare adrenaline.' },
  { range: '2-3', heading: 'Treat', body: 'Administer IM adrenaline · call 000.', critical: true },
  { range: '3-5', heading: 'Support', body: 'Oxygen applied · IV access attempted · monitor vitals.' },
  { range: '5-7', heading: 'Reassess', body: 'Reassess · repeat adrenaline if required.' },
  { range: '7-10', heading: 'Debrief', body: 'Handover simulation · debrief.' },
];

const DEBRIEF = ['What went well', 'What delayed action', 'Any confusion', 'Improvements required'];
const COMPETENCY = ['Recognition of anaphylaxis', 'Correct adrenaline dose (adult + weight-based paediatric)', 'Proper injection technique (IM, lateral thigh, 23G needle)', 'Escalation steps (call for help · call 000 · prepare repeat dose)'];

export default function AnaphylaxisSOPPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          .print-compact { gap: 0.4rem !important; }
          h1, h2, h3 { color: #0E2D5C !important; }
          .dose-card { page-break-inside: avoid; }
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 print-container" data-testid="anaphylaxis-page">
        {/* Top bar */}
        <div className="no-print flex items-center justify-between mb-6">
          <button onClick={() => navigate('/sops')} data-testid="anp-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <button onClick={() => window.print()} data-testid="anp-print" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white" style={{ backgroundColor: RED }}>
            <Printer className="w-3.5 h-3.5" /> Print wall poster
          </button>
        </div>

        {/* RED COVER — designed as poster hero */}
        <div className="rounded-2xl overflow-hidden mb-6" style={{ backgroundColor: DEEP_RED, color: 'white' }}>
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/10 text-white">
                <Zap className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-white/15 text-white">
                Anaphylaxis Protocol · v1.0 · Display in every procedure room
              </span>
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-black tracking-tight">ANAPHYLAXIS — USE FIRST</h1>
            <p className="text-sm mt-2 opacity-90">Advanced clinical toolkit · weight-based dosing · emergency tray · 10-min simulation drill</p>
          </div>
          <div className="bg-black/15 px-6 sm:px-8 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <HeroStat icon={Target} label="Adrenaline target" value="60-90 seconds" />
            <HeroStat icon={Syringe} label="Route" value="IM · LATERAL THIGH · 23G" />
            <HeroStat icon={Clock} label="Repeat adrenaline" value="every 5 minutes if required" />
          </div>
        </div>

        {/* ADRENALINE — the hero section — impossible to miss */}
        <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold text-white" style={{ backgroundColor: RED }}>1</span>
          First-Line · IM Adrenaline 1:1000
        </h2>

        {/* Adult dose — prominent */}
        <div className="rounded-2xl border-2 p-6 sm:p-8 mb-4 dose-card" style={{ borderColor: RED, backgroundColor: `${RED}0A` }} data-testid="anp-adult-dose">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] mb-1" style={{ color: RED }}>Adult</p>
              <p className="font-['Outfit'] text-5xl sm:text-6xl font-black" style={{ color: DEEP_RED }}>0.5 mg IM</p>
              <p className="text-sm text-foreground mt-1 font-semibold">= 0.5 mL of 1:1000 · Lateral thigh</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">Repeat</p>
              <p className="font-['Outfit'] text-2xl font-bold" style={{ color: DEEP_RED }}>every 5 min</p>
              <p className="text-xs text-muted-foreground">if no improvement</p>
            </div>
          </div>
        </div>

        {/* Paediatric dose table */}
        <div className="rounded-2xl border-2 p-5 sm:p-6 mb-4 dose-card" style={{ borderColor: RED, backgroundColor: `${RED}06` }} data-testid="anp-paeds-dose">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3" style={{ color: RED }}>Paediatric — weight-based IM 1:1000</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PAEDIATRIC.map((p, i) => (
              <div key={i} className="rounded-xl border bg-card p-4" style={{ borderColor: `${RED}33` }}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Weight</p>
                <p className="font-['Outfit'] text-xl font-bold text-foreground">{p.w}</p>
                <div className="mt-3 pt-3 border-t" style={{ borderColor: `${RED}20` }}>
                  <p className="font-['Outfit'] text-2xl font-black" style={{ color: DEEP_RED }}>{p.dose}</p>
                  <p className="text-xs text-foreground mt-0.5 font-mono">{p.vol} of 1:1000</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key rules */}
        <div className="rounded-xl border p-4 mb-8" style={{ borderColor: AMBER, backgroundColor: `${AMBER}0A` }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] mb-2" style={{ color: AMBER }}>Key Rules</p>
          <ul className="space-y-1 text-sm text-foreground">
            <li className="flex gap-2"><span className="font-bold" style={{ color: AMBER }}>•</span><span><strong>IM route ONLY initially.</strong> Not IV.</span></li>
            <li className="flex gap-2"><span className="font-bold" style={{ color: AMBER }}>•</span><span>Use <strong>lateral thigh</strong>, NOT the deltoid.</span></li>
            <li className="flex gap-2"><span className="font-bold" style={{ color: AMBER }}>•</span><span>Repeat if no improvement.</span></li>
          </ul>
        </div>

        {/* Second-line — clearly labeled as NOT a substitute */}
        <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold text-white" style={{ backgroundColor: BLUE }}>2</span>
          Second-Line — adjuncts only
        </h2>
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 mb-4 text-sm text-foreground">
          <strong>⚠ These are NOT substitutes for adrenaline.</strong>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="anp-second-line">
          {SECOND_LINE.map((s, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4" style={{ color: BLUE }} />
                <p className="text-sm font-bold" style={{ color: BLUE }}>{s.label}</p>
              </div>
              <p className="text-xs text-foreground/85 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        {/* Emergency tray layout */}
        <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold text-white" style={{ backgroundColor: RED }}>3</span>
          Emergency Drug Tray — standardised layout
        </h2>
        <div className="rounded-2xl border-2 p-5 sm:p-6 mb-8" style={{ borderColor: RED }} data-testid="anp-tray">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] px-3 py-1.5 rounded-md text-white mb-4" style={{ backgroundColor: RED }}>
            <Zap className="w-3 h-3" /> Tray Label: ANAPHYLAXIS — USE FIRST
          </div>
          <p className="text-xs text-muted-foreground mb-4">Arrange the tray in <strong>exactly this order</strong> so there is no searching during an emergency.</p>

          <div className="space-y-2 print-compact">
            {TRAY.map(t => (
              <div key={t.n} className="flex items-start gap-4 rounded-xl border p-4" style={t.highlight
                ? { borderColor: t.tone, backgroundColor: `${t.tone}10` }
                : { borderColor: `${t.tone}33`, backgroundColor: `${t.tone}06` }}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 font-['Outfit'] text-xl font-black text-white" style={{ backgroundColor: t.tone }}>{t.n}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground">{t.label}</p>
                  <p className="text-xs text-foreground/75 mt-0.5 leading-relaxed">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-5 pt-5 border-t border-border">
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Co-located monitoring</p>
              <p className="text-sm text-foreground">BP cuff · Pulse oximeter</p>
            </div>
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 mb-1">Daily check requirement</p>
              <p className="text-sm text-foreground">Checked daily · Signed checklist · Expiry dates verified</p>
            </div>
          </div>
        </div>

        {/* 10-minute drill */}
        <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold text-white" style={{ backgroundColor: BLUE }}>4</span>
          10-Minute Simulation Drill <span className="text-xs font-medium text-muted-foreground">· every 3 months · all staff</span>
        </h2>
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-4" data-testid="anp-drill">
          <p className="text-xs text-muted-foreground mb-3"><strong>Scenario:</strong> Patient undergoing CT contrast develops sweating, hypotension, shortness of breath.</p>
          <div className="space-y-2">
            {DRILL.map((d, i) => (
              <div key={i} className="flex gap-3 rounded-lg border p-3" style={d.critical
                ? { borderColor: RED, backgroundColor: `${RED}0A` }
                : { borderColor: 'hsl(var(--border))' }}>
                <div className="shrink-0 w-16 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">min</p>
                  <p className="font-['Outfit'] text-lg font-bold" style={{ color: d.critical ? RED : NAVY }}>{d.range}</p>
                </div>
                <div className="flex-1 min-w-0 border-l pl-3" style={{ borderColor: d.critical ? `${RED}40` : 'hsl(var(--border))' }}>
                  <p className="text-sm font-bold text-foreground">{d.heading}</p>
                  <p className="text-xs text-foreground/85 mt-0.5 leading-relaxed">{d.body}</p>
                  {d.call && (
                    <p className="mt-1.5 inline-block px-2 py-1 rounded-md font-['Outfit'] text-xs font-black uppercase tracking-wider text-white" style={{ backgroundColor: RED }}>
                      {d.call}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance target */}
        <div className="rounded-2xl border-2 p-5 mb-8 flex items-center gap-4" style={{ borderColor: RED, backgroundColor: `${RED}0A` }} data-testid="anp-target">
          <Target className="w-8 h-8 shrink-0" style={{ color: RED }} />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: RED }}>Performance target</p>
            <p className="font-['Outfit'] text-2xl font-black text-foreground">Adrenaline given within 60-90 seconds</p>
          </div>
        </div>

        {/* Debrief + Competency — side by side */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="anp-debrief">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Debrief — after every drill or real event</p>
            <ul className="space-y-2 text-sm text-foreground">
              {DEBRIEF.map(d => (
                <li key={d} className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />{d}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-5" data-testid="anp-competency">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Competency sign-off — each staff member</p>
            <ul className="space-y-2 text-sm text-foreground">
              {COMPETENCY.map(c => (
                <li key={c} className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />{c}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Real-world insight */}
        <div className="rounded-xl border p-5 mb-8" style={{ borderColor: `${NAVY}25`, backgroundColor: `${NAVY}06` }}>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2" style={{ color: NAVY }}>Real-world insight</p>
          <p className="text-sm text-foreground leading-relaxed">
            In actual events, staff <strong>don't forget what to do</strong> — they forget <strong>where things are</strong>, or hesitate 5-10 seconds too long. The standardised tray layout, the red "ANAPHYLAXIS — USE FIRST" label, and the 60-90s adrenaline target exist to remove both problems.
          </p>
        </div>

        <p className="no-print text-center text-[11px] text-muted-foreground mb-2">
          Owner: Medical Director · Governance: Practice Manager · Review: Annual · Drill: Every 3 months
        </p>
      </div>
    </div>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 opacity-90 shrink-0" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-70">{label}</p>
        <p className="font-['Outfit'] text-base font-bold">{value}</p>
      </div>
    </div>
  );
}
